import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation, useNavigate } from "react-router-dom";

import { Button, IconButton, Popover, Whisper } from "rsuite";
import InfoRoundIcon from "@rsuite/icons/InfoRound";
import HistoryIcon from "@rsuite/icons/History";
import StorageIcon from "@rsuite/icons/Storage";

import { ReactComponent as Home } from "assets/images/home.svg";

import useUrlToPathConversion from "hooks/useUrlToPathConvertation";

import constants from "utils/constants";
import { type IConvertedItem, urlConvertor } from "utils/url-helper";

import { addErpPackage } from "api/deliverables/erp-packages-api";
import { addEmailTemplate } from "api/deliverables/email-templates-api";

import { type RootState, type AppDispatch } from "store/store";
import { erpPackageDefault } from "store/deliverables/erp-packages/erp-packages-types";
import { emailTemplateDefault } from "store/deliverables/email-templates/email-templates-types";
import {
    setActiveEnvironmentById,
    setActiveEnvironmentTenantsMonikers,
    setAppRegistrationInfoOpened,
} from "store/environments/environment-actions";
import {
    getIsActiveTenantSet,
    getTenantDisplayName,
    getTenantMoniker,
} from "store/environments/tenants/tenants-selectors";
import { getCustomerName } from "store/customers/customers-selectors";
import { getPartnerName } from "store/partners/partners-selectors";
import {
    getActiveEnvironmentId,
    getActiveEnvironmentName,
    getIsActiveEnvironmentSet,
    getIsDeleteAllowed,
    getIsEnrollAllowed,
} from "store/environments/environment-selectors";
import {
    getIsEmailTemplatesAllowed,
    getIsPackagesModificationAllowed,
    getIsUserPartner,
} from "store/app-global/app-global-selectors";
import { setIsModalOpen } from "store/common/modal-window/modal-window-slices";
import { setAddNewGuestTenant } from "store/environments/tenants/tenant-guest-tenants/tenant-guest-tenants-actions";
import {
    setAddNewPartner,
    setIsPartnerHistoryOpened,
} from "store/partners/partners-actions";
import {
    setManagePackageDetails,
    setManagePackageOpened,
} from "store/deliverables/erp-packages/erp-packages-actions";
import { setEnvironmentHistoryOpened } from "store/environments/environment-history/environment-history-actions";
import { setIsCustomerHistoryOpened } from "store/customers/customers-actions";
import {
    setManageTemplateDetails,
    setManageTemplateOpened,
} from "store/deliverables/email-templates/email-templates-actions";
import {
    setCreateOpen,
    getInProgress,
} from "store/environments/tenants/mailboxes/mailboxes-slices";

import DeleteTenantBarContainer from "../Environments/Tenants/DeleteTenant/DeleteTenantBar/DeleteTenantBarContainer";
import DeleteTenantEnrollBarContainer from "../Environments/Tenants/DeleteTenant/DeleteTenantEnrollBar/DeleteTenantEnrollBarContainer";
import DeletePartnerButtonContainer from "../Partners/DeletePartnerButton/DeletePartnerButtonContainer";
import DeleteCustomerButtonContainer from "components/Customers/CustomerDetails/DeleteCustomerButton/DeleteCustomerButtonContainer";

import RoutesMap from "./BreadCrumbRouteMap";

import BreadCrumbSwitch from "./BreadCrumbSwitch";

import css from "./BreadCrumb.module.css";

const BreadCrumb: React.FC = () => {
    const { t } = useTranslation();
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const tenantDisplayName = useSelector((state: RootState) =>
        getTenantDisplayName(state)
    );
    const tenantMoniker = useSelector((state: RootState) =>
        getTenantMoniker(state)
    );
    const customerName = useSelector((state: RootState) =>
        getCustomerName(state)
    );
    const partnerName = useSelector((state: RootState) =>
        getPartnerName(state)
    );
    const environmentId = useSelector((state: RootState) =>
        getActiveEnvironmentId(state)
    );
    const environmentName = useSelector((state: RootState) =>
        getActiveEnvironmentName(state)
    );
    const isEnrollAllowed = useSelector((state: RootState) =>
        getIsEnrollAllowed(state)
    );
    const isDeleteAllowed = useSelector((state: RootState) =>
        getIsDeleteAllowed(state)
    );
    // TODO: Should be used permission-based approach
    const isUserPartner = useSelector((state: RootState) =>
        getIsUserPartner(state)
    );
    const isActiveEnvironment = useSelector((state: RootState) =>
        getIsActiveEnvironmentSet(state)
    );
    const isActiveTenant = useSelector((state: RootState) =>
        getIsActiveTenantSet(state)
    );
    const isPackagesModificationAllowed = useSelector((state: RootState) =>
        getIsPackagesModificationAllowed(state)
    );
    const isTemplatesAccessAllowed = useSelector((state: RootState) =>
        getIsEmailTemplatesAllowed(state)
    );

    const canHandleMailboxes = !useSelector((state: RootState) =>
        getInProgress(state)
    );

    const { route, params } = useUrlToPathConversion(pathname);
    const currentMappedRoute = RoutesMap.find((r) => r.path === route);
    let convertedArr: IConvertedItem[] = [];
    if (currentMappedRoute) {
        convertedArr = [...urlConvertor(currentMappedRoute.breadcrumb, params)];
    }

    const setBreadCrumbText = (parameter: string, text: string) => {
        let itemIndex: number | undefined;
        const convertedItem = convertedArr.find((convertedItem, index) => {
            if (convertedItem.name === parameter) {
                itemIndex = index;

                return true;
            }

            return false;
        });

        if (convertedItem && typeof itemIndex === "number") {
            convertedItem.name = text;
            convertedArr[itemIndex] = convertedItem;
        }
    };

    if (params.environmentId && environmentName) {
        setBreadCrumbText(params.environmentId, environmentName);
    }

    if (params.tenantId && tenantDisplayName && tenantMoniker) {
        setBreadCrumbText(
            params.tenantId,
            `${tenantMoniker} - ${tenantDisplayName}`
        );
    }

    if (params.customerId && customerName) {
        setBreadCrumbText(params.customerId, customerName);
    }

    if (params.partnerId && partnerName) {
        setBreadCrumbText(params.partnerId, partnerName);
    }

    if (currentMappedRoute?.path === constants.routePath.storageAccounts) {
        convertedArr[1].name = t("storageAccounts.title");
    }

    if (
        currentMappedRoute?.path === constants.routePath.enrollTenant ||
        currentMappedRoute?.path === constants.routePath.enrollNewTenant
    ) {
        convertedArr[1].name = t("enrollTenant.pageTitle");
    }

    if (currentMappedRoute?.path === constants.routePath.guestTenantsEnroll) {
        convertedArr[1].name = t("enrollTenant.pageTitle");
        convertedArr[2].name = t("enrollTenant.guestTenants.stepName");
    }

    const actionAppInfoRoutes = [constants.routePath.tenants];
    const actionStorageRoutes = [constants.routePath.tenants];
    const actionHistoryRoutes = [
        constants.routePath.tenants,
        constants.routePath.customerDetails,
        constants.routePath.partnerDetails,
    ];

    const homeRoute = "/" + route?.split("/")[1];

    const addNewCustomerBtn = route === constants.routePath.customers && (
        <Button
            onClick={() => {
                dispatch(setIsModalOpen(true));
            }}
            appearance="primary"
            className={css.addNewCustomerBtn}
        >
            {t("customers.addNewCustomer")}
        </Button>
    );

    const enrollTenantBtn = route === constants.routePath.tenants &&
        isEnrollAllowed && (
            <NavLink to={`/environments/${environmentId}/enroll-new-tenant`}>
                <Button appearance="primary" className={css.addNewCustomerBtn}>
                    {t("enrollTenant.enrollTenantBtn")}
                </Button>
            </NavLink>
        );

    const newGuestTenant = (route === constants.routePath.guestTenants ||
        route === constants.routePath.guestTenantsEnroll) &&
        isActiveEnvironment &&
        isActiveTenant && (
            <Button
                onClick={() => {
                    dispatch(setAddNewGuestTenant(true));
                }}
                appearance="primary"
                className={css.addNewCustomerBtn}
            >
                {t("tenantConfig.guestTenants.newGuestTenantButton")}
            </Button>
        );

    const newPartner = route === constants.routePath.partners &&
        !isUserPartner && (
            <Button
                onClick={() => {
                    dispatch(setAddNewPartner(true));
                }}
                appearance="primary"
                className={css.addNewCustomerBtn}
            >
                {t("partners.newPartner")}
            </Button>
        );

    const handleNewPackageClick = () => {
        dispatch(setManagePackageOpened(true));
        dispatch(
            setManagePackageDetails({
                title: t("deliverables.packages.add.title"),
                confirmButtonName: t("deliverables.packages.add.create"),
                erpPackage: erpPackageDefault,
                apiMethod: addErpPackage,
            })
        );
    };

    const newPackage = route === constants.routePath.deliverablesPackages &&
        isPackagesModificationAllowed && (
            <Button
                onClick={handleNewPackageClick}
                appearance="primary"
                className={css.addNewCustomerBtn}
            >
                {t("deliverables.packages.add.title")}
            </Button>
        );

    const actionHistory = actionHistoryRoutes.some((r) => r === route) && (
        <IconButton
            size="lg"
            className={css.actionButton}
            icon={<HistoryIcon />}
            onClick={() => {
                switch (route) {
                    case constants.routePath.tenants:
                        dispatch(setEnvironmentHistoryOpened(true));
                        break;
                    case constants.routePath.customerDetails:
                        dispatch(setIsCustomerHistoryOpened(true));
                        break;
                    case constants.routePath.partnerDetails:
                        dispatch(setIsPartnerHistoryOpened(true));
                        break;
                }
            }}
        />
    );

    const actionStorage = actionStorageRoutes.some((r) => r === route) &&
        !isUserPartner && (
            <IconButton
                size="lg"
                className={css.actionButton}
                icon={<StorageIcon />}
                onClick={() => {
                    navigate(`/environments/${environmentId}/storage-accounts`);
                }}
            />
        );

    const actionAppInfo = actionAppInfoRoutes.some((r) => r === route) && (
        <IconButton
            size="lg"
            className={css.actionButton}
            icon={<InfoRoundIcon />}
            onClick={() => {
                dispatch(setAppRegistrationInfoOpened(true));
            }}
        />
    );

    const handleNewTemplateClick = () => {
        dispatch(setManageTemplateOpened(true));
        dispatch(
            setManageTemplateDetails({
                title: t("deliverables.emailTemplates.modal.header"),
                confirmButtonName: t(
                    "deliverables.emailTemplates.modal.create"
                ),
                actionName: t("deliverables.emailTemplates.createdAction"),
                emailTemplate: emailTemplateDefault,
                apiMethod: addEmailTemplate,
            })
        );
    };

    const newEmailTemplate = route ===
        constants.routePath.deliverablesTemplates &&
        isTemplatesAccessAllowed && (
            <Button
                onClick={handleNewTemplateClick}
                appearance="primary"
                className={css.addNewCustomerBtn}
            >
                {t("deliverables.emailTemplates.newTemplateButton")}
            </Button>
        );

    const createMailboxesBtn = route ===
        constants.routePath.captureMailboxes && (
        <Button
            onClick={() => {
                dispatch(setCreateOpen(true));
            }}
            appearance="primary"
            className={css.addNewCustomerBtn}
            disabled={!canHandleMailboxes}
        >
            {t("tenantConfig.captureMailboxes.mailboxCreation.title")}
        </Button>
    );

    const deleteBar = isDeleteAllowed &&
        route?.startsWith(constants.routePath.tenantConfigs) &&
        isActiveEnvironment &&
        isActiveTenant && <DeleteTenantBarContainer />;

    const deleteEnrollBar = isDeleteAllowed &&
        (route?.startsWith(constants.routePath.enrollTenant) ||
            route?.startsWith(constants.routePath.enrollNewTenant)) && (
            <DeleteTenantEnrollBarContainer />
        );

    const deletePartnerButton = route ===
        constants.routePath.partnerDetails && <DeletePartnerButtonContainer />;

    const deleteCustomerButton = route ===
        constants.routePath.customerDetails && (
        <DeleteCustomerButtonContainer />
    );

    useEffect(() => {
        if (params.environmentId && environmentId !== params.environmentId) {
            dispatch(setActiveEnvironmentById(params.environmentId));
            dispatch(setActiveEnvironmentTenantsMonikers());
        }
    }, [dispatch, environmentId, params.environmentId]);

    return (
        <div className={css.container}>
            <NavLink to={homeRoute} className={css.item}>
                <Home />
            </NavLink>

            {convertedArr &&
                convertedArr.length !== 0 &&
                convertedArr.map((item: IConvertedItem, index: number) => {
                    return item.name.length > constants.itemNameLengthLimit ? (
                        <Whisper
                            trigger="hover"
                            placement={"bottomStart"}
                            key={String(index)}
                            speaker={
                                <Popover
                                    className="tenant-card_popover"
                                    visible={true}
                                >
                                    {`${item.name}`}
                                </Popover>
                            }
                        >
                            <BreadCrumbSwitch item={item} index={index} />
                        </Whisper>
                    ) : (
                        <>
                            <BreadCrumbSwitch item={item} index={index} />
                        </>
                    );
                })}

            <div className={css.actionsPanel}>
                {deleteEnrollBar ||
                    deleteBar ||
                    deletePartnerButton ||
                    deleteCustomerButton}
                {addNewCustomerBtn ||
                    enrollTenantBtn ||
                    newGuestTenant ||
                    newEmailTemplate ||
                    newPackage ||
                    newPartner ||
                    createMailboxesBtn}
                {actionHistory}
                {actionAppInfo}
                {actionStorage}
            </div>
        </div>
    );
};

export default BreadCrumb;
